import _ from "lodash";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { mergeClass, removeTags } from "../../utils";
import { useEffect, useState } from "react";
import Select from "react-select";
import { isVideo } from "../Upload";
import {
  getThumbFilename,
  uploadFile,
  getStillVideoFrameFromURL,
  getImageThumbnailFromURL,
} from "./FileHandling/Utils";
import { tMsg } from "../ToastNotifications";
import { Spinner } from "../Loader";
import { useAtomValue } from "jotai";
import { hostDataAtom, useModifyUploadList } from "../../atoms";
import { IDropdownOption, dropdownFields } from "../../utils/customisation";
import { StringArrayField } from "./stringArrayComponents";
import { fieldId } from "./utils";
import DateRangeSelector from "./dateRangeComponent/DateRangeSelector";
import { format } from "date-fns";
// import { DateRangeComponent } from "./dateRangeComponent";

interface IBaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  modelname?: string;
  type?: keyof typeof FieldList;
  onChange: (value: any) => void;

  options?: IDropdownOption[];

  thumbUrl?: string;
  setThumbUrl?: (value: string) => void;
}

const FieldName = ({
  name,
  hint,
  required,
}: {
  name: string;
  hint?: string | React.ReactNode;
  required?: boolean;
}): JSX.Element => (
  <div>
    <div className="font-medium">
      {name}
      {required && <span className="text-red-500 font-bold"> *</span>}
    </div>
    {hint && <div className="text-xs text-gray-500">{hint}</div>}
  </div>
);

const TextOrNumber = ({
  name,
  value,
  ...restProps
}: IBaseInputProps): JSX.Element => (
  <input
    id={fieldId(name)}
    value={value || ""}
    className="w-full border border-[#ccc] p-2.5 text-sm"
    {...restProps}
  />
);

const StringArray = (props: IBaseInputProps): JSX.Element => {
  const { name, onChange, value } = props;
  const useValue = _.isArray(value) ? value : [];
  return <StringArrayField name={name} onChange={onChange} array={useValue} />;
};

const DateRange = (props: IBaseInputProps): JSX.Element => {
  return <DateRangeSelector {...props} />;
};

const Checkbox = (props: IBaseInputProps): JSX.Element => {
  const { name, value, onChange } = props;
  const checkboxVal = _.isBoolean(value) && value === true;
  return (
    <input
      type="checkbox"
      className="h-4 w-3.5"
      id={fieldId(name)}
      checked={checkboxVal}
      onChange={onChange}
    />
  );
};

const RichTextInput = (props: IBaseInputProps): JSX.Element => {
  const countWords = (text: any) => {
    if (_.isString(text)) {
      const cleanHtml = text.replaceAll(/<[^>]+>/g, "");
      return _.words(cleanHtml).length;
    } else if (_.isNumber(text)) return _.words(text.toString()).length;
    else return 0;
  };
  const { name, value, required, onChange } = props;
  const [wordCount, setWordCount] = useState<number>(countWords(value));

  const modules = {
    // toolbar: [["bold", "italic"], ["clean"]],
    toolbar: [["italic"], ["clean"]],
  };
  const getValue = _.isString(value) ? value : "";

  // Attribute fonts
  const Font = ReactQuill.Quill.import("attributors/class/font");
  Font.whitelist = ["Manrope"];
  ReactQuill.Quill.register(Font, true);

  // FIX: ofApp only accepts <i> tag for italics, but quill uses <em> by default, this fixes that
  // Source: https://github.com/KillerCodeMonkey/ngx-quill/issues/101#issuecomment-358270459
  const Inline = ReactQuill.Quill.import("blots/inline");
  class ItalicBlot extends Inline {}
  ItalicBlot.blotName = "italic";
  ItalicBlot.tagName = "i";
  ReactQuill.Quill.register(ItalicBlot);

  const clearAllPressed = () => {
    const obj = { target: { value: "" } };
    onChange(obj);
  };

  return (
    <>
      <div className="w-full h-[20.8rem] relative">
        <ReactQuill
          id={fieldId(name)}
          className="w-full h-72"
          theme="snow"
          onChange={(e) => {
            setWordCount(countWords(e));
            const withoutTags = _.trim(removeTags(e));
            const value = withoutTags.length < 1 ? "" : e;
            const obj = { target: { value } };
            onChange(obj);
          }}
          modules={modules}
          value={getValue}
          formats={["italic"]}
        />
        <input // This is a hidden field to register the 'required' for the form function
          id={`${fieldId(name)} copy`}
          value={_.trim(removeTags(getValue))}
          required={required}
          type="text"
          className="w-full h-full absolute top-0 left-0 pointer-events-none bg-transparent text-transparent"
        />
      </div>
      <div className="text-xs flex justify-between">
        <div>Words: {wordCount}</div>
        <div className={"text-red-500 opacity-60 group hover:opacity-100"}>
          <button
            onClick={(e) => {
              e.preventDefault();
              clearAllPressed();
            }}
            className="group-hover:underline"
          >
            Clear all
          </button>
        </div>
      </div>
    </>
  );
};

const Dropdown = (props: IBaseInputProps): JSX.Element => {
  const { name, options, className, value, onChange, modelname, required } =
    props;
  const useOptions =
    options ||
    (modelname &&
      dropdownFields.find((d) => d.model === modelname && d.field === name)
        ?.options) ||
    [];
  const dropdownValue = useOptions?.find((o) => o.value === value) || null;
  return (
    <Select
      name={name}
      options={useOptions}
      value={dropdownValue}
      className={mergeClass(className)}
      onChange={(e) => {
        // console.log(e);
        const obj = { target: { value: e?.value } };
        onChange(obj);
      }}
      required={required}
    />
  );
};

const FileUpload = ({ thumbUrl, ...props }: IBaseInputProps): JSX.Element => {
  const { name, onChange, value, accept, required, type } = props;

  const selectorId = `file_select_${name}`;
  const getFieldId = fieldId(name);

  const [file, setFile] = useState<File | null>(null);
  // const [imgUrl, setImgUrl] = useState<string>(_.isString(value) ? value : "");
  const [uploadText, setUploadText] = useState<string>(
    _.isString(value) ? value : ""
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const hostData = useAtomValue(hostDataAtom);

  const [addToList, removeFromList] = useModifyUploadList(getFieldId);

  const thumbSrc = thumbUrl || (_.isString(value) && value);

  // Update upload preview from remote source on element load
  useEffect(() => {
    return () => removeFromList();
  }, []);

  const fileSelected = (selectedFile: File | null) => {
    if (selectedFile !== null) {
      const newFilename = `${format(new Date(), "yyyyMMddHHmmss")}_${
        selectedFile.name
      }`;
      const stampedFile = new File([selectedFile], newFilename);
      setFile(stampedFile);
      startFileUpload(stampedFile); // don't handle on button click anymore
    } else {
      setFile(null);
    }
  };

  const startFileUpload = async (selectedFile: File | null) => {
    try {
      if (selectedFile === null) {
        tMsg.error("Unable to upload selected file");
        return;
      }
      setIsUploading(true);
      addToList();
      setUploadText(`Uploading ${selectedFile.name}`);
      uploadFile(
        selectedFile,
        onUploadFinished,
        hostData.awsBucket,
        hostData.awsRegion,
        updateProgress
      );
    } catch (err) {
      tMsg.error("Error uploading file. See console for details");
      console.log(err);
      setIsUploading(false);
      setUploadText("No file selected");
      setFile(null);
      setIsUploading(false);
      removeFromList();
    }
  };

  const onUploadFinished = (fileLocation: string) => {
    const obj = { target: { value: fileLocation } };
    onChange(obj);
    setUploadText(fileLocation);
    setFile(null);
    setIsUploading(false);
    removeFromList();
    console.log("uploaded file", fileLocation);
  };

  const updateProgress = (amount: number) => {
    setUploadText(`Uploading ${file?.name} - ${amount}%`);
  };

  return (
    <div className="w-full flex relative">
      <input
        id={getFieldId}
        name={name}
        value={value}
        type="text"
        onChange={(e) => e.preventDefault()}
        required={required}
        className="absolute -z-10 w-full h-full"
      />
      <input
        type="file"
        onChange={(e) =>
          fileSelected(e.target.files ? e.target.files[0] : null)
        }
        className="w-full"
        accept={accept}
        id={selectorId}
        data-has-url={value !== ""}
        data-file-url={value}
        data-is-direct
        hidden
      />
      <div
        id="thumb_container"
        className="w-12 h-auto bg-gray-300 overflow-hidden"
      >
        <img
          src={thumbSrc || "/assets/default-image.jpg"}
          className={mergeClass(`object-cover w-full h-full`, {
            "opacity-40": !value || file !== null,
          })}
        />
      </div>
      <div className="flex-grow border border-[#ccc] grid content-center px-2.5 text-sm">
        <div className="flex gap-3 overflow-x-auto">
          <span
            className={mergeClass("break-words", {
              "opacity-50": file !== null || !value,
            })}
          >
            {uploadText}
          </span>
          {isUploading && <Spinner />}
        </div>
      </div>
      <label
        htmlFor={selectorId}
        className={mergeClass(
          "px-5 py-2 grid bg-primaryColor border-primaryColor border text-white font-medium",
          "transition hover:bg-white hover:text-primaryColor hover:border-primaryColor cursor-pointer"
        )}
      >
        {!thumbSrc && !file ? "Select file" : "Change file"}
      </label>
    </div>
  );
};

const VideoUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "video/mp4"} />
);
const ImageUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "image/jpg, image/png"} />
);

const FieldList = {
  number: TextOrNumber,
  string: TextOrNumber,
  string_100: TextOrNumber,
  string_200: TextOrNumber,
  string_400: TextOrNumber,
  bool: Checkbox,
  boolean: Checkbox,
  wysiwyg: RichTextInput,
  file: FileUpload,
  image: ImageUpload,
  video: VideoUpload,
  dropdown: Dropdown,
  string_array: StringArray,
  date_range_array: DateRange,
};

const DefaultFieldList = {
  number: null,
  string: "",
  boolean: null,
  wysiwyg: "",
  file: null,
  image: "",
  video: "",
  dropdown: null,
  string_array: [],
  date_range_array: [],
};

const isValidFieldType = (k: string): k is keyof typeof FieldList => {
  return k in FieldList;
};

export { FieldName, FieldList, DefaultFieldList, isValidFieldType, Dropdown };
export type { IBaseInputProps, IDropdownOption };
